import { formatDistanceToNow } from "date-fns";
import { CircleDot } from "lucide-react";

import EmptyData from "../state-pages/EmptyData";
import Loading from "../state-pages/Loading";

import { ExpandableText } from "./ExpandableText";

import { EstimateActivityActions } from "@/constants/constants";
import { IActivity } from "@/interfaces/activity.interface";
import { formatToCapitalizedWords, renderFullName } from "@/lib/utils";
import {
  DATE_TIME_FORMAT,
  YEAR_MONTH_DAY_FORMAT,
  dateFormat,
  todayYesterDay,
} from "@/utils/helper-functions/date-time.helper";

export interface ActivityItem {
  date: string;
  action: string;
  by: string;
  on: string;
  detail: string;
}

export interface GroupedData {
  date: string;
  activities: ActivityItem[];
}

const CustomActivity = ({
  data,
  lastItemRef,
  isFetching,
  className,
}: {
  data: IActivity[];
  lastItemRef?: (node: HTMLDivElement | null) => void;
  isFetching?: boolean;
  className?: string;
}): React.JSX.Element => {
  const groupedData: GroupedData[] = [];

  data?.forEach((item) => {
    const found = groupedData?.find(
      (gd) =>
        dateFormat(gd?.date, YEAR_MONTH_DAY_FORMAT) ===
        dateFormat(item?.createdAt, YEAR_MONTH_DAY_FORMAT),
    );
    const fullName = renderFullName({
      params: {
        firstName: item?.actor?.firstName || undefined,
        middleName: item?.actor?.middleName || undefined,
        lastName: item?.actor?.lastName || undefined,
      },
    });

    const role = item?.actor?.role || "USER";

    if (found) {
      found?.activities?.push({
        date: item?.createdAt ?? "",
        action: item?.action ?? "",
        by: `${fullName} (${formatToCapitalizedWords(role)})`,
        on: item?.activityEntity ?? "",
        detail: item?.detail ?? "",
      });
    } else {
      groupedData?.push({
        date: item?.createdAt,
        activities: [
          {
            date: item?.createdAt,
            action: item?.action ?? "",
            by: `${fullName} (${formatToCapitalizedWords(role)})`,
            on: item?.activityEntity ?? "",
            detail: item?.detail ?? "",
          },
        ],
      });
    }
  });

  const GetColoredIcon = (
    action: EstimateActivityActions,
  ): { icon: React.JSX.Element; color: string } => {
    switch (action) {
      case EstimateActivityActions.CREATE:
      case EstimateActivityActions.VERIFIED:
      case EstimateActivityActions.SYNCED:
      case EstimateActivityActions.SET_FINAL:
        return {
          icon: (
            <div className='p-1 bg-[#28C76F]/20  rounded-full'>
              <CircleDot size={12} color='#28C76F' fill='#28C76F' />
            </div>
          ),
          color: "#28C76F",
        };
      case EstimateActivityActions.SET_REVISION:
      case EstimateActivityActions.MOVED:
        return {
          icon: (
            <div className='p-1 bg-[#6610F2]/20  rounded-full'>
              <CircleDot size={12} color='#6610F2' fill='#6610F2' />
            </div>
          ),
          color: "#6610F2",
        };
      case EstimateActivityActions.STATUS_CHANGED:
      case EstimateActivityActions.UPDATE:
        return {
          icon: (
            <div className='p-1 bg-[#00CFE8]/20  rounded-full'>
              <CircleDot size={12} color='#00CFE8' fill='#00CFE8' />
            </div>
          ),
          color: "#00CFE8",
        };

      default:
        return {
          icon: (
            <div className='p-1 bg-[#DA0619]/20  rounded-full'>
              <CircleDot size={12} color='#DA0619' fill='#DA0619' />
            </div>
          ),
          color: "#DA0619",
        };
    }
  };

  if (data?.length === 0) {
    return (
      <EmptyData title='Activity' subtitle='There is no activity available' />
    );
  }

  const getName = (action: EstimateActivityActions): string => {
    switch (action) {
      case EstimateActivityActions.CREATE:
        return "Created";
      case EstimateActivityActions.SET_REVISION:
        return "Revised";
      case EstimateActivityActions.UPDATE:
        return "Updated";
      case EstimateActivityActions.DELETE:
        return "Deleted";
      case EstimateActivityActions.SYNCED:
        return "Synced";
      case EstimateActivityActions.MOVED:
        return "Moved";
      default:
      case EstimateActivityActions.VERIFIED:
        return "Verified";
      case EstimateActivityActions.SET_FINAL:
        return "Finalized";
      case EstimateActivityActions.SET_NOT_FINAL:
        return "Not Finalized";
      case EstimateActivityActions.STATUS_CHANGED:
        return "Status Changed";
    }
  };

  return (
    <div>
      {groupedData?.map((activity, id) => (
        <div className='mt-4 space-y-6 text-aldgray' key={`${id}-activity`}>
          <h1 className={`text-xl font-medium ${className}`}>
            {todayYesterDay(activity.date)}
          </h1>
          <div className='relative'>
            {activity?.activities?.map((singleActivity, index) => (
              <div
                key={index}
                className='relative flex justify-between gap-5 pb-4'
              >
                <div className='absolute bg-primary/10 h-full w-[1px] -top-2 left-[18px] -z-10' />
                <div className='flex items-center flex-1 min-w-0 gap-4'>
                  <div className='p-2 rounded-full bg-primary-50 dark:bg-background dark:border text-primary shrink-0'>
                    {
                      GetColoredIcon(
                        singleActivity?.action as unknown as EstimateActivityActions,
                      ).icon
                    }
                  </div>
                  <div className='space-y-1.5 min-w-0 max-w-[calc(100%-5rem)]'>
                    <h2
                      style={{
                        color: GetColoredIcon(
                          singleActivity.action as unknown as EstimateActivityActions,
                        ).color,
                      }}
                      className='max-w-full text-base truncate text-dark-500'
                    >
                      {getName(
                        singleActivity.action as unknown as EstimateActivityActions,
                      )}
                    </h2>

                    <ExpandableText
                      text={`${singleActivity?.by} ${singleActivity?.detail}`}
                    />
                  </div>
                </div>

                {/* Date (Right Side) */}
                <p className='my-auto ml-5 text-xs font-semibold text-dark-500 shrink-0 whitespace-nowrap'>
                  {dateFormat(singleActivity?.date, DATE_TIME_FORMAT)}{" "}
                  <span className='font-medium text-primary'>
                    (
                    {formatDistanceToNow(singleActivity?.date, {
                      addSuffix: true,
                    })}
                    )
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div
        ref={lastItemRef}
        className='flex items-center justify-center w-full my-6'
      >
        {isFetching && <Loading className='py-2' />}
      </div>
    </div>
  );
};

export default CustomActivity;
